import { QueryFactory } from '@lmt/shared-types';
import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import {
  PatientSearch,
  SearchInputPatientData,
} from '@lmt/unicorn-shared/app/components/common/patient/PatientSearch';
import {
  Button,
  ButtonColor,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';
import { DatePicker } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/date-time/DatePicker';
import { DropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/DropDownInput';
import styles from '@lmt/unicorn-shared/app/components/audit/AuditFilter.module.scss';
import { useUsers } from '@lmt/unicorn-shared/app/features/users/selectors';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { useMemo } from 'react';
import { ReactComponent as ResetIcon } from '@lmt/unicorn-shared/app/icons/reset.svg';
import { useSubtenants } from '../../features/subtenants/selectors';
import { MultiSelectDropDownInput } from '../common/ui-kit/inputs/dropdown/MultiSelectDropDownInput';

type Props = {
  search: string | null;
  from: Date | null;
  to: Date | null;
  patientId: number | null;
  user: string | null;
  subtenantIds: number[] | undefined | null;
  table: string | null;
  searchKey: number;
  onFromChanged: (date: Date | null) => void;
  onToChanged: (date: Date | null) => void;
  onSearchChanged?: (value: string) => void;
  onPatientChanged?: (value: SearchInputPatientData | null) => void;
  onUserChanged: (value: string | null) => void;
  onTableChanged: (value: string | null) => void;
  onSubtenantsChanged: (value: number[] | null) => void;
  onReset: () => void;
};

export const AuditFilter: React.FC<Props> = React.memo(
  function AuditFilter(props) {
    const i18n = useScopedTranslation('audit');
    const users = useUsers();
    const subtenants = useSubtenants();
    const tableNamesQuery = QueryFactory.AuditQuery.useGetTableNamesQuery();
    const tables = useMemo(
      () => (tableNamesQuery.data ?? []).map((x) => ({ value: x })),
      [tableNamesQuery.data],
    );
    const { data: patient, isFetching } =
      QueryFactory.PatientQuery.useGetPatientQuery(props.patientId!, {
        enabled: !!props.patientId,
      });

    return (
      <div data-test-id="messages-filter" className={styles.container}>
        <div className={styles.header}>
          {i18n.t('filter.title').toUpperCase()}
        </div>
        <Field title={i18n.t('filter.table')}>
          <DropDownInput
            variant={'normal'}
            useIdFunctionAsValue={true}
            required={false}
            getOptionLabel="value"
            idFunction="value"
            options={tables}
            value={props.table}
            onValueChanged={(value) => {
              props.onTableChanged(value?.value ?? null);
            }}
            placeholder={i18n.t('filter.table_placeholder')}
          />
        </Field>
        <Field title={i18n.t('filter.patient')}>
          <PatientSearch
            key={'patient' + props.searchKey}
            isOnlyActive={true}
            onSelectedPatientChanged={props.onPatientChanged!}
            selectedPatient={props.patientId ? (patient ?? null) : null}
            patientsLimit={9}
            autoFocus={true}
            variant={'normal'}
            selectPatientIfOnlyOneFound={true}
            data-variant="normal"
            className={styles.patientSearch}
          />
        </Field>
        {subtenants.length > 1 && (
          <Field title={i18n.t('filter.subtenants')} className={styles.field}>
            <MultiSelectDropDownInput
              variant="normal"
              getOptionLabel="name"
              idFunction="id"
              options={subtenants}
              values={props.subtenantIds}
              onValueChanged={(option) => {
                props.onSubtenantsChanged(option!.map((x) => x.id));
              }}
            />
          </Field>
        )}
        <Field title={i18n.t('filter.user')}>
          <DropDownInput
            useIdFunctionAsValue={true}
            required={false}
            variant="normal"
            getOptionLabel="firstName"
            idFunction="id"
            options={users}
            value={props.user}
            onValueChanged={(value) => {
              props.onUserChanged(value?.id ?? null);
            }}
            placeholder={i18n.t('filter.user_placeholder')}
          />
        </Field>
        <Field title={i18n.t('filter.start_date')}>
          <DatePicker
            value={props.from}
            onDateChanged={props.onFromChanged}
            withTime={true}
            isFormInput={false}
            placeholder={i18n.t('filter.start_date')}
          />
        </Field>
        <Field title={i18n.t('filter.end_date')}>
          <DatePicker
            value={props.to}
            withTime={true}
            onDateChanged={props.onToChanged}
            placeholder={i18n.t('filter.end_date')}
          />
        </Field>
        <div className={styles.buttonContainer}>
          <Button
            iconComponent={<ResetIcon />}
            color={ButtonColor.Primary}
            className={styles.button}
            title={i18n.t('refresh_button')}
            onClick={props.onReset}
          />
        </div>
      </div>
    );
  },
);
